import React, { useState } from "react";
import {
    Line,
    Bar,
    Pie,
    Doughnut,
    Radar,
    Scatter,
    Bubble,
} from "react-chartjs-2";
import "chart.js/auto";
import HeatMap from "react-heatmap-grid"; // For Heatmaps
import HighchartsReact from "highcharts-react-official"; // For complex charts
import Highcharts from "highcharts"; // Core Highcharts library
import "../../../css/ChartCard.css";
import useRequestDetailedData from "../../../utils/useRequestDetailedData";

const ChartCard = ({ item }) => {
    const {
        requestResponseData,
        loading,
        error,
    } = useRequestDetailedData(item.requestId, item, true);

    const [selectedRange, setSelectedRange] = useState("today");

    const timeRanges = [
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "This Week", value: "this_week" },
        { label: "Last Week", value: "last_week" },
        { label: "This Month", value: "this_month" },
        { label: "Last Month", value: "last_month" },
        { label: "This Year", value: "this_year" },
        { label: "Last Year", value: "last_year" },
    ];

    // Add chart color palette
    const chartColors = {
        backgrounds: [
            'rgba(124, 92, 252, 0.2)',  // Purple
            'rgba(45, 183, 245, 0.2)',  // Blue
            'rgba(37, 204, 156, 0.2)',  // Green
            'rgba(255, 184, 0, 0.2)',   // Yellow
            'rgba(255, 96, 96, 0.2)',   // Red
            'rgba(255, 128, 192, 0.2)', // Pink
            'rgba(84, 112, 198, 0.2)',  // Navy
            'rgba(145, 200, 50, 0.2)',  // Lime
        ],
        borders: [
            'rgb(124, 92, 252)',    // Purple
            'rgb(45, 183, 245)',    // Blue
            'rgb(37, 204, 156)',    // Green
            'rgb(255, 184, 0)',     // Yellow
            'rgb(255, 96, 96)',     // Red
            'rgb(255, 128, 192)',   // Pink
            'rgb(84, 112, 198)',    // Navy
            'rgb(145, 200, 50)',    // Lime
        ]
    };

    // Update chart options to include hover effects
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                align: 'end',
                labels: {
                    boxWidth: 12,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    color: 'var(--text-color)',
                    padding: 8,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                backgroundColor: 'var(--card-bg)',
                titleColor: 'var(--text-color)',
                bodyColor: 'var(--text-color)',
                borderColor: 'var(--border-color)',
                borderWidth: 1,
                padding: 12,
                boxPadding: 6,
                usePointStyle: true,
                callbacks: {
                    labelPointStyle: (context) => ({
                        pointStyle: 'circle',
                        rotation: 0
                    })
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false
                },
                ticks: {
                    color: 'var(--text-secondary)',
                    font: {
                        size: 11
                    }
                }
            },
            y: {
                grid: {
                    color: 'var(--border-color)',
                    drawBorder: false,
                    borderDash: [5, 5]
                },
                ticks: {
                    color: 'var(--text-secondary)',
                    font: {
                        size: 11
                    },
                    padding: 8
                }
            }
        },
        layout: {
            padding: {
                top: 0,
                right: 8,
                bottom: 8,
                left: 8
            }
        }
    };

    const handleTimeRangeChange = (e) => {
        setSelectedRange(e.target.value);
    };

    const getChartData = () => {
        if (!requestResponseData || requestResponseData.length === 0) return null;

        const { xAxis, yAxis, label, value, ...restMappings } = item.chartMappings;

        const labels = xAxis
            ? requestResponseData.map((row) => row[xAxis])
            : requestResponseData.map((row) => row[label]);

        const datasets = (yAxis || [value]).map((field, index) => ({
            label: field,
            data: requestResponseData.map((row) => row[field]),
            backgroundColor: chartColors.backgrounds[index % chartColors.backgrounds.length],
            borderColor: chartColors.borders[index % chartColors.borders.length],
            borderWidth: 2,
            tension: 0.4,
            pointStyle: 'circle',
            pointRadius: 4,
            pointBackgroundColor: '#fff',
            pointBorderWidth: 2,
            fill: true,
            // For bar charts
            hoverBackgroundColor: chartColors.borders[index % chartColors.borders.length],
            hoverBorderColor: chartColors.borders[index % chartColors.borders.length],
            // For pie/doughnut charts
            ...(item.chartType === 'PIE' || item.chartType === 'DOUGHNUT' ? {
                backgroundColor: chartColors.borders,
                borderColor: 'white',
                borderWidth: 2
            } : {})
        }));

        return { labels, datasets };
    };

    const chartData = getChartData();

    const renderHeatmap = () => {
        const { row, column, value } = item.chartMappings;
        const xLabels = [...new Set(requestResponseData.map((d) => d[column]))];
        const yLabels = [...new Set(requestResponseData.map((d) => d[row]))];

        const data = yLabels.map((yLabel) =>
            xLabels.map(
                (xLabel) =>
                    requestResponseData.find(
                        (d) => d[row] === yLabel && d[column] === xLabel
                    )?.[value] || 0
            )
        );

        return (
            <HeatMap
                xLabels={xLabels}
                yLabels={yLabels}
                data={data}
                background={(value) =>
                    `rgba(0, 151, 230, ${value / Math.max(...data.flat())})`
                }
                height={30}
                width={50}
            />
        );
    };

    const renderHighcharts = (options) => {
        return <HighchartsReact highcharts={Highcharts} options={options} />;
    };

    const renderChart = () => {
        if (!chartData) return <div>No data available to display the chart.</div>;

        switch (item.chartType) {
            case "BAR":
            case "COLUMN":
                return (
                    <Bar 
                        data={chartData}
                        options={{
                            ...chartOptions,
                            barThickness: 20,
                            maxBarThickness: 30
                        }}
                    />
                );
            case "LINE":
            case "AREA":
                return (
                    <Line
                        data={chartData}
                        options={chartOptions}
                    />
                );
            case "STACKED_BAR":
                return (
                    <Bar
                        data={chartData}
                        options={{
                            plugins: { legend: { position: "top" } },
                            scales: { x: { stacked: true }, y: { stacked: true } },
                        }}
                    />
                );
            case "PIE":
                return <Pie data={chartData} />;
            case "DOUGHNUT":
                return <Doughnut data={chartData} />;
            case "RADAR":
                return <Radar data={chartData} />;
            case "SCATTER":
                return (
                    <Scatter
                        data={{
                            datasets: chartData.datasets.map((dataset) => ({
                                label: dataset.label,
                                data: dataset.data.map((y, index) => ({
                                    x: chartData.labels[index],
                                    y,
                                })),
                                backgroundColor: dataset.backgroundColor,
                            })),
                        }}
                    />
                );
            case "BUBBLE":
                return (
                    <Bubble
                        data={{
                            datasets: chartData.datasets.map((dataset) => ({
                                label: dataset.label,
                                data: dataset.data.map((value, index) => ({
                                    x: requestResponseData[index][item.chartMappings.xAxis],
                                    y: requestResponseData[index][item.chartMappings.yAxis],
                                    r: requestResponseData[index][item.chartMappings.size],
                                })),
                                backgroundColor: dataset.backgroundColor,
                            })),
                        }}
                    />
                );
            case "HEATMAP":
                return renderHeatmap();
            case "TREEMAP":
                return renderHighcharts({
                    series: [
                        {
                            type: "treemap",
                            layoutAlgorithm: "squarified",
                            data: requestResponseData.map((d) => ({
                                name: d[item.chartMappings.category],
                                value: d[item.chartMappings.value],
                            })),
                        },
                    ],
                });
            case "SANKEY":
                return renderHighcharts({
                    series: [
                        {
                            type: "sankey",
                            keys: ["from", "to", "weight"],
                            data: requestResponseData.map((d) => [
                                d[item.chartMappings.source],
                                d[item.chartMappings.target],
                                d[item.chartMappings.value],
                            ]),
                        },
                    ],
                });
            default:
                return <div>Chart type {item.chartType} is not supported.</div>;
        }
    };

    if (loading) {
        return <div className="chart-loading">Loading...</div>;
    }

    if (error) {
        return <div className="chart-error">Error: {error}</div>;
    }

    return (
        <div className="chart-card">
            <div className="chart-header">
                <div className="chart-title">
                    <h5>{item.name}</h5>
                    {item.description && (
                        <p className="text-secondary">{item.description}</p>
                    )}
                </div>
                {item.range && (
                    <select
                        className="time-range-select"
                        value={selectedRange}
                        onChange={(e) => setSelectedRange(e.target.value)}
                    >
                        {timeRanges.map((range) => (
                            <option key={range.value} value={range.value}>
                                {range.label}
                            </option>
                        ))}
                    </select>
                )}
            </div>
            <div className="chart-body">
                {renderChart()}
            </div>
        </div>
    );
};

export default ChartCard;
